"use no memo";

import type { Row } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef } from "react";

import type { features } from "~/components/common/tickets/ticket-list";
import { TicketListItem } from "~/components/common/tickets/ticket-list-item";
import type { Ticket } from "~/stores/tickets-store";

export const TICKET_ITEM_HEIGHT = 44;

export function TicketSimpleList({
  rows,
}: {
  rows: Row<typeof features, Ticket>[];
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => TICKET_ITEM_HEIGHT,
    getScrollElement: () => parentRef.current,
    overscan: 25,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const [paddingTop, paddingBottom] =
    virtualItems.length > 0
      ? [
          Math.max(
            0,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            virtualItems[0]!.start - virtualizer.options.scrollMargin,
          ),
          Math.max(
            0,
            virtualizer.getTotalSize() -
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              virtualItems[virtualItems.length - 1]!.end,
          ),
        ]
      : [0, 0];

  return (
    <div
      ref={parentRef}
      className="col-[1/_-1] grid min-h-0 grid-cols-subgrid overflow-x-hidden overflow-y-auto"
    >
      <div
        className="col-[1/_-1] grid min-h-0 grid-cols-subgrid content-start"
        style={{
          height: virtualizer.getTotalSize(),
        }}
      >
        {paddingTop > 0 ? <div style={{ height: paddingTop }}></div> : null}
        {virtualItems.map((virtualItem) => {
          const row = rows[virtualItem.index];
          if (!row) return null;
          return (
            <TicketListItem
              row={row}
              key={virtualItem.key}
              data-index={virtualItem.index}
              data-row-id={row.id}
              data-list-key={`ITEM_${row.original.id}`}
              ref={virtualizer.measureElement}
            />
          );
        })}
        {paddingBottom > 0 ? (
          <div style={{ height: paddingBottom }}></div>
        ) : null}
      </div>
    </div>
  );
}
